import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { getIssueCategory } from '../../../constants/issueTypes';
import { messagingActions } from '../../../reducers/messagingSlice';
import { selectIsWebSocketConnected } from '../../../selectors/message';
import {
  buildAudioWindow,
  buildIssueReportContext,
  buildIssueReportEnvelopeIdentity,
  ISSUE_REPORT_CLIENT_VERSION,
} from '../../../utils/issueReportContext';
import {
  buildIssueReportEnvelope,
  configureIssueReportTransport,
  deliverIssueReportEnvelope,
} from '../../../utils/issueReportTransport';
import logger from '../../../utils/logger';
import {
  IssueReportContext,
  IssueReportEventData,
  IssueReportMetadata,
  IssueReportRecord,
  IssueReportSource,
  IssueReportState,
} from './issueReport.props';

/**
 * HITL-1492 Pillar 3, Part A (Capture).
 *
 * Two invariants this slice exists to hold:
 *
 * 1. `context` is captured once on press and never re-read. The task-router teardown
 *    wipes the session state a later classification would need.
 * 2. Records SURVIVE session teardown. Nothing here reacts to resetAI,
 *    resetSelectedRestaurantDetails, or the restaurant/stage/menu-version resets that
 *    sessionBoundary listens for — HITL-1501's unclassified chip and the pre-lobby
 *    panel both need reports to outlive the session. The only reset is the existing
 *    `user/logout` wipe in rootReducer, which is shift-end and correct.
 */

/**
 * Growth bound in place of a session reset. The event is already sent by the time a
 * record would be evicted, so eviction only loses the Undo/amend affordance on an
 * ancient record — never a report.
 */
export const MAX_RETAINED_REPORTS = 50;

/** How long the flag window stays open for Undo and the quick-pick tray. */
export const FLAG_WINDOW_MS = 5000;

export const initialState: IssueReportState = {
  byId: {},
  order: [],
  activeReportId: null,
  activeWindowExpiresAt: null,
  stagedIssueType: null,
  stagedRank: null,
};

const evictOldest = (state: IssueReportState) => {
  while (state.order.length > MAX_RETAINED_REPORTS) {
    const oldest = state.order.shift();
    if (oldest) {
      delete state.byId[oldest];
      if (state.activeReportId === oldest) {
        state.activeReportId = null;
        state.activeWindowExpiresAt = null;
      }
    }
  }
};

/**
 * Build the wire payload for a record at its CURRENT revision.
 *
 * `context` rides along on revision 0 and on any later revision whose revision 0 was
 * never acknowledged. Without that second condition a lost rev 0 plus a landed rev 1
 * would leave a classified report with no session join at all — no session_id, no
 * store_code — which is precisely the failure this pillar exists to eliminate. It
 * costs nothing because the context is already frozen on the record, so it survives
 * the task-router teardown that makes the amend context-free in the first place.
 */
export const buildIssueReportEventData = (
  record: IssueReportRecord,
  issueTypes = undefined as Parameters<typeof getIssueCategory>[1]
): IssueReportEventData => {
  const includeContext = record.revision === 0 || !record.rev0Delivered;
  return {
    schema_version: '1.0',
    client_version: ISSUE_REPORT_CLIENT_VERSION,
    report_id: record.report_id,
    revision: record.revision,
    voided: record.voided,
    source: record.source,
    issue_type: record.issue_type,
    issue_category: getIssueCategory(record.issue_type, issueTypes),
    details: record.details,
    ranked_candidates: record.ranked_candidates,
    chosen_rank: record.chosen_rank,
    context: includeContext ? record.context : null,
    artifacts: {
      screenshot_url: null,
      audio_window_ms: includeContext ? buildAudioWindow(record.context) : null,
    },
    metadata: record.metadata,
  };
};

/**
 * Flat scalars only, namespaced keys, bounded — enforced here rather than by
 * convention, because convention will not hold across four tickets.
 */
export const METADATA_MAX_KEYS = 20;
export const METADATA_MAX_KEY_LENGTH = 64;
export const METADATA_MAX_VALUE_LENGTH = 256;

export const sanitizeIssueReportMetadata = (
  metadata?: Record<string, unknown> | null
): IssueReportMetadata => {
  if (!metadata) return {};
  const dropped: string[] = [];
  const sanitized: IssueReportMetadata = {};

  Object.entries(metadata).forEach(([key, value]) => {
    if (Object.keys(sanitized).length >= METADATA_MAX_KEYS) {
      dropped.push(key);
      return;
    }
    if (!key || key.length > METADATA_MAX_KEY_LENGTH) {
      dropped.push(key);
      return;
    }
    if (value === null || typeof value === 'boolean') {
      sanitized[key] = value;
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      sanitized[key] = value;
      return;
    }
    if (typeof value === 'string') {
      sanitized[key] = value.slice(0, METADATA_MAX_VALUE_LENGTH);
      return;
    }
    // Objects, arrays, functions, undefined, NaN: a nested bag is where an entire
    // undeclared sub-schema hides, so it is dropped rather than flattened.
    dropped.push(key);
  });

  if (dropped.length) {
    logger.warn({
      message: `Dropped ${dropped.length} issue-report metadata key(s): must be a flat scalar within the size caps.`,
      droppedKeys: dropped.join(','),
    });
  }
  return sanitized;
};

const issueReportSlice = createSlice({
  name: 'issueReport',
  initialState,
  reducers: {
    reportCreated: (state, action: PayloadAction<IssueReportRecord>) => {
      const record = action.payload;
      state.byId[record.report_id] = record;
      state.order.push(record.report_id);
      state.activeReportId = record.report_id;
      state.activeWindowExpiresAt =
        Date.parse(record.created_at) + FLAG_WINDOW_MS;
      state.stagedIssueType = null;
      state.stagedRank = null;
      evictOldest(state);
    },
    reportAmended: (
      state,
      action: PayloadAction<{
        report_id: string;
        issue_type: string | null;
        details: string | null;
        chosen_rank: number | null;
      }>
    ) => {
      const { report_id, issue_type, details, chosen_rank } = action.payload;
      const record = state.byId[report_id];
      if (!record) return;
      record.revision += 1;
      record.issue_type = issue_type;
      record.details = details;
      record.chosen_rank = chosen_rank;
      record.status = 'pending';
    },
    reportVoided: (state, action: PayloadAction<{ report_id: string }>) => {
      const record = state.byId[action.payload.report_id];
      if (!record) return;
      // Idempotent (HITL-1499): a double-click on the toast's Undo inside one
      // frame would otherwise bump the revision twice and emit TWO retractions,
      // at revisions 1 and 2. One retraction per report, whatever the agent does
      // to the button.
      if (record.voided) return;
      record.revision += 1;
      record.voided = true;
      record.status = 'pending';
    },
    sendSucceeded: (
      state,
      action: PayloadAction<{ report_id: string; revision: number }>
    ) => {
      const { report_id, revision } = action.payload;
      const record = state.byId[report_id];
      if (!record) return;
      if (revision === 0) {
        record.rev0Delivered = true;
      }
      // Only the newest revision governs the record's status; a late ack for an older
      // revision must not mark a freshly amended record as fully sent.
      if (revision >= record.revision) {
        record.status = 'sent';
      }
    },
    sendFailed: (
      state,
      action: PayloadAction<{ report_id: string; revision: number }>
    ) => {
      const record = state.byId[action.payload.report_id];
      if (!record) return;
      record.status = 'failed';
    },
    openFlagWindow: (
      state,
      action: PayloadAction<{ report_id: string; expiresAt: number }>
    ) => {
      state.activeReportId = action.payload.report_id;
      state.activeWindowExpiresAt = action.payload.expiresAt;
    },
    closeFlagWindow: (state) => {
      state.activeReportId = null;
      state.activeWindowExpiresAt = null;
      state.stagedIssueType = null;
      state.stagedRank = null;
    },
    /**
     * Stage a quick-pick choice. Records NOTHING and sends NOTHING — this is view
     * state that lives here so the toast and the tray read one source of truth. Only
     * commitIssueType mutates the record, which is what makes HITL-1500's "no type is
     * recorded that the agent did not choose" true even when the tray times out.
     */
    stageIssueType: (
      state,
      action: PayloadAction<{ issue_type: string; rank: number | null }>
    ) => {
      state.stagedIssueType = action.payload.issue_type;
      state.stagedRank = action.payload.rank;
    },
    clearStagedIssueType: (state) => {
      state.stagedIssueType = null;
      state.stagedRank = null;
    },
  },
});

export const {
  reportCreated,
  reportAmended,
  reportVoided,
  sendSucceeded,
  sendFailed,
  openFlagWindow,
  closeFlagWindow,
  stageIssueType,
  clearStagedIssueType,
} = issueReportSlice.actions;

/** Injected by HITL-1500; absent here, so `ranked_candidates` stays empty. */
export type IssueRanker = (context: IssueReportContext) => string[];

/**
 * Wire the transport and hand it the current record so it can be delivered.
 *
 * The websocket mirror fires at revision 0 ONLY, and only while the socket is open:
 * after teardown `messagingTransformMiddleware` would stamp a wiped restaurant_code
 * and a stale endedSessionId. It is additive — consumers must dedupe on
 * `(report_id, revision)`, never on envelope id.
 */
const deliver = createAsyncThunk(
  'issueReport/deliver',
  async ({ reportId }: { reportId: string }, { getState, dispatch }) => {
    const state = getState();
    const record = state.issueReport.byId[reportId];
    if (!record) return;

    configureIssueReportTransport(
      {
        eventAPI: state.config.EVENT_API,
        eventAPIAuth: state.config.EVENT_API_BASIC_AUTH_PARAM,
      },
      {
        onSuccess: (report_id, revision) =>
          dispatch(sendSucceeded({ report_id, revision })),
        onFailure: (report_id, revision) =>
          dispatch(sendFailed({ report_id, revision })),
      }
    );

    const data = buildIssueReportEventData(
      record,
      state.config.ISSUE_REPORTING_TYPES
    );
    const { email, username, id, firstName, lastName } =
      state.user?.userProfile || ({} as any);

    deliverIssueReportEnvelope(
      buildIssueReportEnvelope({
        data,
        // Frozen at press time, NOT read live: by the time an amend is submitted at
        // the task-router boundary these have been wiped from Redux.
        sessionId: record.envelopeIdentity.session_id,
        restaurantCode: record.envelopeIdentity.restaurant_code,
        laneId: record.envelopeIdentity.lane_id,
        agentMetadata: {
          agent_name: username,
          agent_id: id,
          agent_email: email,
          agent_first_name: firstName,
          agent_last_name: lastName,
        },
      })
    );

    if (record.revision === 0 && selectIsWebSocketConnected(state)) {
      dispatch(messagingActions.sendIssueReport({ data }));
    }
  }
);

/**
 * Flag an issue. O(1) synchronous work on the press path — a uuid, one pure context
 * build, one ranker call — and NO network on this call stack (NFR-1).
 */
export const flagIssue = createAsyncThunk(
  'issueReport/flagIssue',
  async (
    {
      source = 'action_bar' as IssueReportSource,
      rank,
      metadata,
    }: {
      source?: IssueReportSource;
      rank?: IssueRanker;
      metadata?: Record<string, unknown>;
    },
    { getState, dispatch }
  ) => {
    const state = getState();
    const context = buildIssueReportContext(state);
    const reportId = uuidv4();

    // Run ONCE, here, so ranked_candidates is present even when the tray is never
    // shown or is ignored, and so the recorded candidates cannot disagree with the
    // rendered chips.
    let rankedCandidates: string[] = [];
    if (rank) {
      try {
        rankedCandidates = rank(context) || [];
      } catch (error) {
        // A broken ranker must never cost the agent their report.
        logger.warn({
          message: 'Issue report ranker threw; recording no candidates.',
          error: String(error),
        });
      }
    }

    const record: IssueReportRecord = {
      report_id: reportId,
      envelopeIdentity: buildIssueReportEnvelopeIdentity(state),
      revision: 0,
      status: 'pending',
      voided: false,
      rev0Delivered: false,
      source,
      created_at: new Date().toISOString(),
      issue_type: null,
      details: null,
      ranked_candidates: rankedCandidates,
      chosen_rank: null,
      context,
      metadata: sanitizeIssueReportMetadata(metadata),
    };

    dispatch(reportCreated(record));
    dispatch(deliver({ reportId }));
    return reportId;
  }
);

/**
 * Classify a report. One thunk for BOTH classification paths — HITL-1500's tray passes
 * `chosen_rank`, HITL-1501's panel passes `details`. Two thunks would drift on
 * revision handling.
 */
export const commitIssueType = createAsyncThunk(
  'issueReport/commitIssueType',
  async (
    {
      report_id,
      issue_type,
      chosen_rank = null,
      details = null,
    }: {
      report_id: string;
      issue_type: string | null;
      chosen_rank?: number | null;
      details?: string | null;
    },
    { dispatch }
  ) => {
    dispatch(reportAmended({ report_id, issue_type, details, chosen_rank }));
    dispatch(clearStagedIssueType());
    dispatch(deliver({ reportId: report_id }));
  }
);

/**
 * Undo. Emits a RETRACTION at the next revision — it does not cancel the revision-0
 * send, because committing on press is the whole point: a refresh, a car exit or a
 * network blip inside the 5s window would otherwise lose the flag, and blips are
 * exactly what agents are flagging.
 */
export const voidIssue = createAsyncThunk(
  'issueReport/voidIssue',
  async ({ report_id }: { report_id: string }, { getState, dispatch }) => {
    // Guarded here as well as in the reducer: the reducer guard keeps the
    // revision at 1, but without this a second click would still SEND revision 1
    // a second time. Consumers dedupe on (report_id, revision), so that is
    // survivable — it is just a wasted request on the press path.
    const record = getState().issueReport.byId[report_id];
    if (!record || record.voided) return;

    dispatch(reportVoided({ report_id }));
    dispatch(closeFlagWindow());
    dispatch(deliver({ reportId: report_id }));
  }
);

export default issueReportSlice.reducer;
